import { axios } from '@ben/api/axios';
import { optionalNumber, optionalString } from '@ben/shared/utils/zod';
import { createInvalidateHook } from '@ben/shared/hooks/queries/use-query-base';
import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import { isString } from 'lodash';
import { z } from 'zod';

const QUERY_KEY = 'scheduledVersion';

const getKey = (benefitId?: string) => {
  if (!isString(benefitId)) return [QUERY_KEY];
  return [QUERY_KEY, { benefitId }];
};

const scheduledVersionResponseSchema = z.object({
  enrollableBenefit: z
    .object({
      id: optionalNumber,
      scheduledRenewalDate: optionalString,
    })
    .nullable(),
});

type ScheduledVersionResponse = z.infer<typeof scheduledVersionResponseSchema>;

const fetchScheduledVersion = async (benefitId: string) => {
  const response = await axios.get<ScheduledVersionResponse>(
    `benefits/${benefitId}/versions/`,
    {
      schema: scheduledVersionResponseSchema,
    }
  );
  return response.data;
};

export const useQueryScheduledVersion = (
  benefitId?: string,
  options?: UseQueryOptions<ScheduledVersionResponse>
) => {
  return useQuery<ScheduledVersionResponse>({
    queryKey: getKey(benefitId),
    queryFn: () => {
      if (!isString(benefitId)) {
        return Promise.reject(new Error('benefitId must be provided'));
      }
      return fetchScheduledVersion(benefitId);
    },
    enabled: isString(benefitId) && (options?.enabled ?? true),
    ...options,
  });
};

export const useInvalidateScheduledVersion =
  createInvalidateHook<string>(getKey);
